import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ClipboardEvent,
} from 'react';

const LENGTH = 6;

export interface OtpInputHandle {
  /** Focus synchronously (call inside a tap handler so the numeric keyboard stays open). */
  focus(): void;
}

/**
 * One-time code as ONE real, visible input drawn as six cells with CSS. The input carries the
 * value (`autocomplete="one-time-code"`, `inputMode="numeric"`, `pattern="[0-9]*"`,
 * `maxLength=6`) and covers the whole field, so a tap on any cell focuses it and opens the
 * numeric keyboard on iOS; the cells behind it are decoration. Nothing is hidden or
 * off-screen and nothing is focused programmatically on load: the OS autofill, paste and
 * typing all land in the same field. Auto-submits on the sixth digit.
 */
export const OtpInput = forwardRef<
  OtpInputHandle,
  {
    value: string;
    onChange: (code: string) => void;
    onComplete: (code: string) => void;
    disabled?: boolean;
    invalid?: boolean;
    label: string;
  }
>(function OtpInput({ value, onChange, onComplete, disabled, invalid, label }, ref) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  const completedFor = useRef<string | null>(null);
  const active = Math.min(value.length, LENGTH - 1);

  useImperativeHandle(
    ref,
    () => ({ focus: () => inputRef.current?.focus({ preventScroll: true }) }),
    [],
  );

  useEffect(() => {
    if (value.length === LENGTH && completedFor.current !== value) {
      completedFor.current = value;
      onComplete(value);
    }
    if (value.length < LENGTH) completedFor.current = null;
  }, [value, onComplete]);

  const set = (raw: string) => onChange(raw.replace(/\D/g, '').slice(0, LENGTH));

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    set(e.clipboardData.getData('text'));
  };

  return (
    <div
      className={[
        'otp-field control relative h-14 rounded-md border border-line-strong bg-surface',
        invalid ? 'animate-shake' : '',
        disabled ? 'opacity-70' : '',
      ].join(' ')}
      dir="ltr"
      aria-busy={disabled || undefined}
    >
      {/* Cells behind the input (decorative). The input's letter-spacing lines the digits up. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 grid grid-cols-6 gap-2 p-2">
        {Array.from({ length: LENGTH }, (_, i) => {
          const isActive = focused && !disabled && i === active && value.length < LENGTH;
          return (
            <span
              key={i}
              data-cell={i}
              className={[
                'rounded-sm transition-colors duration-[var(--pa-dur-fast)]',
                isActive ? 'bg-brand-soft' : 'bg-canvas',
              ].join(' ')}
            />
          );
        })}
      </div>
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => set(e.target.value)}
        onPaste={onPaste}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        // Read-only (not disabled) while a request is in flight so the keyboard stays open.
        readOnly={disabled}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="one-time-code"
        enterKeyHint="done"
        maxLength={LENGTH}
        aria-label={label}
        aria-invalid={invalid || undefined}
        data-testid="otp-input"
        className={[
          'otp-input tabular relative z-10 h-full w-full bg-transparent text-2xl font-semibold outline-none',
          invalid ? 'text-critical-text' : disabled ? 'text-ink-2' : 'text-ink',
        ].join(' ')}
      />
    </div>
  );
});
